import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
          <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are placed on your computer or mobile device when you 
                visit our website. They help us provide you with a better experience by remembering your 
                preferences and enabling certain functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies for several purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>To remember your login status and preferences</li>
                <li>To analyze how you use our website and improve our services</li>
                <li>To provide personalized content and features</li>
                <li>To ensure security and prevent fraud</li>
                <li>To measure the effectiveness of our marketing campaigns</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Essential Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are necessary for the website to function properly. They enable basic 
                functions like page navigation, access to secure areas, and remembering your login status.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Examples:</strong> Authentication tokens, session identifiers, security cookies
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Performance Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies collect information about how you use our website, such as which pages 
                you visit most often and if you get error messages. This helps us improve our website's performance.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Examples:</strong> Google Analytics, page load times, user journey tracking
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Functionality Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies remember choices you make to improve your experience, such as your 
                language preference or region. They may also be used to provide features you have requested.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Examples:</strong> Language settings, theme preferences, user interface customizations
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 Marketing Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are used to track visitors across websites to display relevant and 
                engaging advertisements. They may also be used to limit the number of times you see an ad.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Examples:</strong> Social media tracking, advertising network cookies, remarketing pixels
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We may use third-party services that set their own cookies. These include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Google Analytics:</strong> To analyze website usage and performance</li>
                <li><strong>Stripe:</strong> To process payments securely</li>
                <li><strong>Social Media Platforms:</strong> For social sharing and login features</li>
                <li><strong>Customer Support Tools:</strong> To provide chat and support functionality</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Cookie Duration</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Session Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are temporary and are deleted when you close your browser. They are 
                used to maintain your session while you navigate through our website.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Persistent Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies remain on your device for a set period or until you delete them. 
                They remember your preferences for future visits.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Managing Cookies</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                You can control and delete cookies through your browser settings. Most browsers 
                allow you to refuse cookies or delete them. However, disabling cookies may affect 
                the functionality of our website.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Cookie Consent</h3>
              <p className="text-gray-700 mb-4">
                When you first visit our website, you'll see a cookie consent banner. You can 
                choose which types of cookies to accept. You can change your preferences at any 
                time through our cookie settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Opt-Out Links</h3>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Google Analytics:</strong> 
                  <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 hover:underline ml-1">
                    Opt out of Google Analytics
                  </a>
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Advertising Networks:</strong> 
                  <a href="http://www.aboutads.info/choices/" className="text-blue-600 hover:underline ml-1">
                    Digital Advertising Alliance
                  </a>
                </p>
                <p className="text-sm text-gray-600">
                  <strong>European Users:</strong> 
                  <a href="http://www.youronlinechoices.eu/" className="text-blue-600 hover:underline ml-1">
                    Your Online Choices
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Impact of Disabling Cookies</h2>
              <p className="text-gray-700 mb-4">
                If you choose to disable cookies, some features of our website may not function properly:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You may need to log in repeatedly</li>
                <li>Your preferences may not be saved</li>
                <li>Some interactive features may not work</li>
                <li>We may not be able to provide personalized content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. We will notify you of any 
                material changes by posting the updated policy on our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@flashai.com<br />
                  <strong>Address:</strong> Flash AI, 123 Business Street, Suite 100, City, State 12345<br />
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
